/**
 * Title Scene: the main menu screen.
 *
 * Displays the game title "LOST IN LABYRINTH" and a keyboard-navigable
 * menu with three options: Play, Continue, and Help.
 *
 * "Continue" is only available when a saved game exists.
 * Press M to toggle the background music on/off.
 */

import Phaser from 'phaser';
import {
  SCENE_TITLE,
  SCENE_GAME,
  SCENE_HELP,
  SCENE_SHOP,
  SCENE_CONFIG,
  SCENE_MANUSCRIPTS,
  SCENE_ACHIEVEMENTS,
  SCENE_RECORDS,
  GameMode,
} from '../utils/constants.js';
import { hasSavedGame, deleteSave, loadMusicPref, saveMusicPref } from '../utils/save-manager.js';
import { GAME_WIDTH, GAME_HEIGHT, COLOR_WHITE, BORDER_MARGIN, AUDIO_TITLE_BG, AUDIO_MENU_CLICK, AUDIO_MENU_SELECT } from '../config/game-config.js';
import { getMusicVol, getSfxVol, applyMasterMute, refreshConfig } from '../utils/audio-config.js';
import { version as APP_VERSION } from '../../package.json';

/** Menu item identifiers. */
const MenuItem = Object.freeze({
  PLAY: 0,
  CONTINUE: 1,
  SHOP: 2,
  CONFIG: 3,
  HELP: 4,
  MANUSCRIPTS: 5,
  ACHIEVEMENTS: 6,
  RECORDS: 7,
});

const TITLE_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '36px',
  color: '#ffffff',
  letterSpacing: 4,
};

const MENU_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '18px',
  color: '#ffffff',
};

const MENU_STYLE_DISABLED = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '18px',
  color: '#666666',
};

const SUBTITLE_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#888888',
};

export default class TitleScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENE_TITLE });
  }

  create() {
    this.cameras.main.setBackgroundColor('#000000');

    // Smooth fade-in from black
    this.cameras.main.fadeIn(400, 0, 0, 0);

    // Refresh audio config (in case user changed settings in ConfigScene)
    refreshConfig();
    applyMasterMute(this.sound);

    // Reset transition lock (the scene instance is reused across restarts,
    // so flags from a previous lifecycle persist if not cleared here)
    this._transitioning = false;

    // Reset game mode sub-menu state
    this._gameModeActive = false;
    this._gameModeElements = null;
    this._gameModeTexts = null;
    this._gameModeDescs = null;
    this._gameModeArrow = null;

    /** @type {number} Currently selected menu index. */
    this.selectedIndex = 0;

    /** @type {boolean} */
    this.hasSave = hasSavedGame();

    /** @type {boolean} Whether music is enabled. */
    this._musicEnabled = loadMusicPref();

    this._createTitle();
    this._createMenu();
    this._createFooter();
    this._createMusicIcon();
    this._setupInput();

    // Decorative border
    this._drawBorder();

    // Background music
    this._startMusic();
  }

  // ---------------------------------------------------------------
  // Music
  // ---------------------------------------------------------------

  /** @private Start or resume the title background music. */
  _startMusic() {
    // If music is disabled by preference, ensure nothing is playing
    if (!this._musicEnabled) {
      const existing = this.sound.get(AUDIO_TITLE_BG);
      if (existing && existing.isPlaying) {
        existing.stop();
      }
      return;
    }

    // Check if the music is already playing (e.g. returning from Help)
    if (this.sound.get(AUDIO_TITLE_BG)) {
      const existing = this.sound.get(AUDIO_TITLE_BG);
      if (!existing.isPlaying) {
        existing.play();
      }
      this.titleMusic = existing;
      return;
    }

    this.titleMusic = this.sound.add(AUDIO_TITLE_BG, {
      loop: true,
      volume: getMusicVol(0.4),
    });
    this.titleMusic.play();
  }

  /** @private Stop the title background music with a fade out. */
  _stopMusic() {
    if (this.titleMusic && this.titleMusic.isPlaying) {
      // Stop immediately — the scene is about to be destroyed so
      // a tween-based fade would never complete.
      this.titleMusic.stop();
      this.titleMusic = null;
    }
  }

  // ---------------------------------------------------------------
  // UI Creation
  // ---------------------------------------------------------------

  /** @private */
  _createTitle() {
    const cx = GAME_WIDTH / 2;

    // Main title — entrance animation: slide down + fade in
    const titleTop = this.add.text(cx, 140, 'LOST IN', {
      ...TITLE_STYLE,
      fontSize: '28px',
    }).setOrigin(0.5);
    titleTop.setAlpha(0);
    titleTop.setY(120);

    this.tweens.add({
      targets: titleTop,
      alpha: 1,
      y: 140,
      duration: 500,
      delay: 100,
      ease: 'Quad.easeOut',
    });

    const titleBottom = this.add.text(cx, 185, 'LABYRINTH', TITLE_STYLE).setOrigin(0.5);
    titleBottom.setAlpha(0);
    titleBottom.setY(165);

    this.tweens.add({
      targets: titleBottom,
      alpha: 1,
      y: 185,
      duration: 500,
      delay: 250,
      ease: 'Quad.easeOut',
    });

    // Subtle idle float on the main title (after entrance completes)
    this.time.delayedCall(800, () => {
      if (!titleBottom.active) return;
      this.tweens.add({
        targets: titleBottom,
        y: 187,
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    });

    // Decorative line below title — fade in after titles
    const g = this.add.graphics();
    g.setAlpha(0);
    g.lineStyle(1, COLOR_WHITE, 0.4);
    g.lineBetween(cx - 140, 215, cx + 140, 215);

    this.tweens.add({
      targets: g,
      alpha: 1,
      duration: 400,
      delay: 450,
      ease: 'Quad.easeOut',
    });
  }

  /** @private */
  _createMenu() {
    const cx = GAME_WIDTH / 2;
    const startY = 248;
    const spacing = 30;

    const items = [
      { label: 'PLAY', id: MenuItem.PLAY, enabled: true },
      { label: 'CONTINUE', id: MenuItem.CONTINUE, enabled: this.hasSave },
      { label: 'SHOP', id: MenuItem.SHOP, enabled: true },
      { label: 'MANUSCRIPTS', id: MenuItem.MANUSCRIPTS, enabled: true },
      { label: 'ACHIEVEMENTS', id: MenuItem.ACHIEVEMENTS, enabled: true },
      { label: 'RECORDS', id: MenuItem.RECORDS, enabled: true },
      { label: 'CONFIG', id: MenuItem.CONFIG, enabled: true },
      { label: 'HELP', id: MenuItem.HELP, enabled: true },
    ];

    /** @type {Phaser.GameObjects.Text[]} */
    this.menuTexts = [];

    items.forEach((item, index) => {
      const style = item.enabled ? MENU_STYLE : MENU_STYLE_DISABLED;
      const text = this.add.text(cx, startY + index * spacing, item.label, style);
      text.setOrigin(0.5);
      text.setData('menuId', item.id);
      text.setData('enabled', item.enabled);

      // Mouse interaction for enabled items
      if (item.enabled) {
        text.setInteractive({ useHandCursor: true });
        text.on('pointerover', () => {
          if (this._transitioning || this._gameModeActive) return;
          if (this.selectedIndex !== index) {
            this.selectedIndex = index;
            try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.5) }); } catch (_) { /* */ }
            this._positionSelector(true);
          }
        });
        text.on('pointerdown', () => {
          if (this._transitioning || this._gameModeActive) return;
          this.selectedIndex = index;
          this._confirm();
        });
      }

      // Entrance animation: staggered fade-in from left
      text.setAlpha(0);
      text.setX(cx - 15);
      this.tweens.add({
        targets: text,
        alpha: item.enabled ? 1 : 0.5,
        x: cx,
        duration: 300,
        delay: 550 + index * 120,
        ease: 'Quad.easeOut',
      });

      this.menuTexts.push(text);
    });

    // Selector arrow — starts hidden, fades in after menu entrance
    this.selectorArrow = this.add.text(0, 0, '>', {
      ...MENU_STYLE,
      fontSize: '18px',
    }).setOrigin(0.5);
    this.selectorArrow.setAlpha(0);

    // Skip disabled items for the initial selection
    if (!items[this.selectedIndex].enabled) {
      this.selectedIndex = 0;
    }

    // Position selector at the correct final location. Menu texts are mid-entrance
    // animation (x = cx - 15), so we compute the target using their known final x.
    const firstItem = this.menuTexts[this.selectedIndex];
    this.selectorArrow.setPosition(
      cx - firstItem.width / 2 - 20,
      firstItem.y,
    );

    // Fade in the selector after menu items have appeared
    this.tweens.add({
      targets: this.selectorArrow,
      alpha: 1,
      duration: 200,
      delay: 550 + items.length * 120,
      ease: 'Quad.easeOut',
    });
  }

  /** @private */
  _createFooter() {
    const cx = GAME_WIDTH / 2;

    const footer1 = this.add.text(cx, GAME_HEIGHT - 50, 'Use arrow keys to navigate, ENTER to select', SUBTITLE_STYLE)
      .setOrigin(0.5);
    footer1.setAlpha(0);

    // Build the footer line as three separate text objects so "JavocSoft" is
    // independently clickable while the rest remains non-interactive.
    const footerFontSize = '10px';
    const footerColor = '#999999';
    const footerY = GAME_HEIGHT - 30;
    const fullString = `@JavocSoft, v${APP_VERSION} | 2026`;

    // Measure the full string width using a temporary hidden text
    const measure = this.add.text(0, 0, fullString, {
      ...SUBTITLE_STYLE, fontSize: footerFontSize, color: footerColor,
    }).setVisible(false);
    const totalWidth = measure.width;
    measure.destroy();

    // Calculate starting X so the full line is centred
    const startX = cx - totalWidth / 2;

    // Part 1: "@"
    const prefix = this.add.text(startX, footerY, '@', {
      ...SUBTITLE_STYLE, fontSize: footerFontSize, color: footerColor,
    }).setOrigin(0, 0.5);
    prefix.setAlpha(0);

    // Part 2: "JavocSoft" — interactive, brighter, underlined on hover
    const linkText = this.add.text(prefix.x + prefix.width, footerY, 'JavocSoft', {
      ...SUBTITLE_STYLE, fontSize: footerFontSize, color: '#cccccc',
    }).setOrigin(0, 0.5);
    linkText.setAlpha(0);
    linkText.setInteractive({ useHandCursor: true });
    linkText.on('pointerover', () => {
      linkText.setColor('#ffffff');
      linkText.setStyle({ ...linkText.style, textDecoration: 'underline' });
    });
    linkText.on('pointerout', () => {
      linkText.setColor('#cccccc');
      linkText.setStyle({ ...linkText.style, textDecoration: 'none' });
    });
    linkText.on('pointerdown', () => {
      window.open('https://portfolio.javocsoft.com/', '_blank');
    });

    // Part 3: ", v{version} | 2026"
    const suffix = this.add.text(linkText.x + linkText.width, footerY, `, v${APP_VERSION} | 2026`, {
      ...SUBTITLE_STYLE, fontSize: footerFontSize, color: footerColor,
    }).setOrigin(0, 0.5);
    suffix.setAlpha(0);

    // Fade in footer after everything else
    this.tweens.add({
      targets: [footer1, prefix, linkText, suffix],
      alpha: 1,
      duration: 400,
      delay: 900,
      ease: 'Quad.easeOut',
    });
  }

  // ---------------------------------------------------------------
  // Game Mode Sub-menu
  // ---------------------------------------------------------------

  /**
   * Show the game mode selection sub-menu (Classic / Explorer).
   * Hides the main menu and presents two mode options with descriptions.
   * @private
   */
  _showGameModeMenu() {
    if (this._gameModeActive) return;
    this._gameModeActive = true;

    const cx = GAME_WIDTH / 2;

    // Fade out main menu items and selector
    const fadeTargets = [...this.menuTexts, this.selectorArrow];
    fadeTargets.forEach(t => {
      this.tweens.killTweensOf(t);
      this.tweens.add({
        targets: t,
        alpha: 0,
        duration: 200,
        ease: 'Quad.easeIn',
      });
    });

    // Container for all game mode UI elements (for easy cleanup)
    /** @type {Phaser.GameObjects.GameObject[]} */
    this._gameModeElements = [];

    // Build the sub-menu after the main menu fades out
    this.time.delayedCall(250, () => {
      // Sub-menu title
      const modeTitle = this.add.text(cx, 235, 'SELECT GAME MODE', {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '16px',
        color: '#ffffff',
        letterSpacing: 2,
      }).setOrigin(0.5).setAlpha(0);
      this._gameModeElements.push(modeTitle);

      // Decorative line below title
      const lineG = this.add.graphics().setAlpha(0);
      lineG.lineStyle(1, COLOR_WHITE, 0.3);
      lineG.lineBetween(cx - 90, 255, cx + 90, 255);
      this._gameModeElements.push(lineG);

      // Mode items
      const modes = [
        {
          label: 'CLASSIC',
          mode: GameMode.CLASSIC,
          desc: 'Death sends you back to level 1.',
        },
        {
          label: 'EXPLORER',
          mode: GameMode.EXPLORER,
          desc: 'Continue where you died — at a cost.',
        },
      ];

      const modeStartY = 285;
      const modeSpacing = 70;

      /** @type {Phaser.GameObjects.Text[]} */
      this._gameModeTexts = [];
      /** @type {Phaser.GameObjects.Text[]} */
      this._gameModeDescs = [];
      this._gameModeSelectedIndex = 0;

      modes.forEach((m, i) => {
        const y = modeStartY + i * modeSpacing;

        const label = this.add.text(cx, y, m.label, {
          fontFamily: '"Courier New", Courier, monospace',
          fontSize: '18px',
          color: '#ffffff',
        }).setOrigin(0.5).setAlpha(0);
        label.setData('gameMode', m.mode);

        // Mouse interaction for game mode labels
        label.setInteractive({ useHandCursor: true });
        label.on('pointerover', () => {
          if (this._transitioning) return;
          if (this._gameModeSelectedIndex !== i) {
            this._gameModeSelectedIndex = i;
            try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.5) }); } catch (_) { /* */ }
            this._positionGameModeSelector(true);
          }
        });
        label.on('pointerdown', () => {
          if (this._transitioning) return;
          this._gameModeSelectedIndex = i;
          this._confirmGameMode();
        });

        const desc = this.add.text(cx, y + 22, m.desc, {
          fontFamily: '"Courier New", Courier, monospace',
          fontSize: '11px',
          color: '#888888',
        }).setOrigin(0.5).setAlpha(0);

        this._gameModeTexts.push(label);
        this._gameModeDescs.push(desc);
        this._gameModeElements.push(label, desc);
      });

      // Selector arrow for sub-menu
      this._gameModeArrow = this.add.text(0, 0, '>', {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '18px',
        color: '#ffffff',
      }).setOrigin(0.5).setAlpha(0);
      this._gameModeElements.push(this._gameModeArrow);

      // ESC hint
      const escHint = this.add.text(cx, 440, 'ESC to go back', {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '11px',
        color: '#555555',
      }).setOrigin(0.5).setAlpha(0);
      this._gameModeElements.push(escHint);

      // Animate everything in
      const allFadeIn = [modeTitle, lineG, ...this._gameModeTexts, ...this._gameModeDescs, this._gameModeArrow, escHint];
      allFadeIn.forEach((el, idx) => {
        this.tweens.add({
          targets: el,
          alpha: 1,
          duration: 250,
          delay: idx * 60,
          ease: 'Quad.easeOut',
        });
      });

      // Position the selector
      this._positionGameModeSelector(false);
    });
  }

  /**
   * Position the game mode selector arrow at the currently selected mode.
   * @private
   * @param {boolean} animate
   */
  _positionGameModeSelector(animate) {
    if (!this._gameModeArrow || !this._gameModeTexts) return;
    const current = this._gameModeTexts[this._gameModeSelectedIndex];
    const targetX = current.x - current.width / 2 - 20;
    const targetY = current.y;

    if (animate) {
      this.tweens.killTweensOf(this._gameModeArrow);
      this.tweens.add({
        targets: this._gameModeArrow,
        x: targetX,
        y: targetY,
        duration: 120,
        ease: 'Quad.easeOut',
      });
    } else {
      this._gameModeArrow.setPosition(targetX, targetY);
    }
  }

  /**
   * Navigate within the game mode sub-menu.
   * @private
   * @param {number} delta
   */
  _navigateGameMode(delta) {
    try {
      this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio fallback */ }

    const count = this._gameModeTexts.length;
    this._gameModeSelectedIndex = ((this._gameModeSelectedIndex + delta) % count + count) % count;
    this._positionGameModeSelector(true);
  }

  /**
   * Confirm the selected game mode and start the game.
   * @private
   */
  _confirmGameMode() {
    if (this._transitioning) return;
    this._transitioning = true;

    try {
      this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio fallback */ }

    const selectedMode = this._gameModeTexts[this._gameModeSelectedIndex].getData('gameMode');

    this._stopMusic();
    deleteSave();
    this.cameras.main.fadeOut(400, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(SCENE_GAME, { continuing: false, gameMode: selectedMode });
    });
  }

  /**
   * Hide the game mode sub-menu and return to the main menu.
   * @private
   */
  _hideGameModeMenu() {
    if (!this._gameModeActive) return;

    try {
      this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio fallback */ }

    // Fade out and destroy all game mode elements
    if (this._gameModeElements) {
      this._gameModeElements.forEach(el => {
        this.tweens.killTweensOf(el);
        this.tweens.add({
          targets: el,
          alpha: 0,
          duration: 150,
          ease: 'Quad.easeIn',
          onComplete: () => {
            if (el.destroy) el.destroy();
          },
        });
      });
    }

    this._gameModeElements = null;
    this._gameModeTexts = null;
    this._gameModeDescs = null;
    this._gameModeArrow = null;
    this._gameModeActive = false;

    // Fade main menu back in after sub-menu fades out
    this.time.delayedCall(200, () => {
      const fadeTargets = [...this.menuTexts, this.selectorArrow];
      fadeTargets.forEach(t => {
        const targetAlpha = t.getData && !t.getData('enabled') ? 0.5 : 1;
        this.tweens.add({
          targets: t,
          alpha: targetAlpha,
          duration: 250,
          ease: 'Quad.easeOut',
        });
      });
    });
  }

  /** @private */
  _drawBorder() {
    const g = this.add.graphics();
    g.lineStyle(1, COLOR_WHITE, 0.2);
    g.strokeRect(BORDER_MARGIN, BORDER_MARGIN,
      GAME_WIDTH - BORDER_MARGIN * 2, GAME_HEIGHT - BORDER_MARGIN * 2);

    // Inner decorative corners
    const cornerSize = 10;
    const m = BORDER_MARGIN + 5;
    g.lineStyle(1, COLOR_WHITE, 0.4);

    // Top-left
    g.lineBetween(m, m, m + cornerSize, m);
    g.lineBetween(m, m, m, m + cornerSize);

    // Top-right
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m - cornerSize, m);
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m, m + cornerSize);

    // Bottom-left
    g.lineBetween(m, GAME_HEIGHT - m, m + cornerSize, GAME_HEIGHT - m);
    g.lineBetween(m, GAME_HEIGHT - m, m, GAME_HEIGHT - m - cornerSize);

    // Bottom-right
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m - cornerSize, GAME_HEIGHT - m);
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m, GAME_HEIGHT - m - cornerSize);
  }

  // ---------------------------------------------------------------
  // Music Icon
  // ---------------------------------------------------------------

  /** @private Create the music toggle icon in the bottom-right corner. */
  _createMusicIcon() {
    const x = GAME_WIDTH - BORDER_MARGIN - 22;
    const y = GAME_HEIGHT - BORDER_MARGIN - 22;

    // ♪ icon
    this._musicIcon = this.add.text(x, y, '\u266A', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '16px',
      color: this._musicEnabled ? '#ffffff' : '#555555',
    }).setOrigin(0.5, 0.5);

    // Strikethrough graphics (red diagonal line when muted)
    this._musicStrike = this.add.graphics();
    this._drawMusicStrike();

    // Make clickable
    this._musicIcon.setInteractive({ useHandCursor: true });
    this._musicIcon.on('pointerdown', () => this._toggleMusic());
    this._musicIcon.on('pointerover', () => this._musicIcon.setAlpha(0.7));
    this._musicIcon.on('pointerout', () => this._musicIcon.setAlpha(1));

    // Fade in with the rest of the UI
    this._musicIcon.setAlpha(0);
    this._musicStrike.setAlpha(0);
    this.tweens.add({
      targets: [this._musicIcon, this._musicStrike],
      alpha: 1,
      duration: 400,
      delay: 900,
      ease: 'Quad.easeOut',
    });
  }

  /** @private Toggle title music on/off and update the icon. */
  _toggleMusic() {
    this._musicEnabled = !this._musicEnabled;
    saveMusicPref(this._musicEnabled);

    // Update icon style
    this._musicIcon.setStyle({
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '16px',
      color: this._musicEnabled ? '#ffffff' : '#555555',
    });
    this._drawMusicStrike();

    // Start or stop the music
    if (this._musicEnabled) {
      this._startMusic();
    } else {
      const existing = this.sound.get(AUDIO_TITLE_BG);
      if (existing && existing.isPlaying) {
        existing.stop();
      }
      this.titleMusic = null;
    }
  }

  /**
   * Draw or clear the red strikethrough line over the music icon.
   * @private
   */
  _drawMusicStrike() {
    this._musicStrike.clear();
    if (!this._musicEnabled) {
      const bounds = this._musicIcon.getBounds();
      this._musicStrike.lineStyle(1.5, 0xff0000, 0.7);
      this._musicStrike.lineBetween(
        bounds.x - 1,
        bounds.y + bounds.height - 2,
        bounds.x + bounds.width + 1,
        bounds.y + 2,
      );
    }
  }

  // ---------------------------------------------------------------
  // Input
  // ---------------------------------------------------------------

  /** @private */
  _setupInput() {
    this.input.keyboard.on('keydown-UP', () => this._navigate(-1));
    this.input.keyboard.on('keydown-DOWN', () => this._navigate(1));
    this.input.keyboard.on('keydown-ENTER', () => this._confirm());
    this.input.keyboard.on('keydown-SPACE', () => this._confirm());
    this.input.keyboard.on('keydown-ESC', () => this._handleEsc());
    this.input.keyboard.on('keydown-M', () => this._toggleMusic());
  }

  /** @private Handle ESC key — returns from sub-menu to main menu. */
  _handleEsc() {
    if (this._transitioning) return;
    if (this._gameModeActive) {
      this._hideGameModeMenu();
    }
  }

  /** @private */
  _navigate(delta) {
    if (this._transitioning) return;

    // Route to game mode sub-menu if active
    if (this._gameModeActive) {
      this._navigateGameMode(delta);
      return;
    }

    try {
      this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio fallback */ }

    let next = this.selectedIndex + delta;
    const count = this.menuTexts.length;

    // Wrap and skip disabled items
    for (let i = 0; i < count; i++) {
      next = ((next % count) + count) % count;
      if (this.menuTexts[next].getData('enabled')) {
        break;
      }
      next += delta;
    }

    this.selectedIndex = ((next % count) + count) % count;
    this._positionSelector(true);
  }

  /**
   * Move the selector arrow to the currently selected menu item.
   * @private
   * @param {boolean} animate - Whether to tween or teleport.
   */
  _positionSelector(animate) {
    const current = this.menuTexts[this.selectedIndex];
    const targetX = current.x - current.width / 2 - 20;
    const targetY = current.y;

    // Ensure the arrow is visible — the entrance tween (delayed alpha fade-in)
    // may have been killed by a prior killTweensOf call if the user navigated
    // before the entrance animation completed.
    this.selectorArrow.setAlpha(1);

    if (animate) {
      this.tweens.killTweensOf(this.selectorArrow);
      this.tweens.add({
        targets: this.selectorArrow,
        x: targetX,
        y: targetY,
        duration: 120,
        ease: 'Quad.easeOut',
      });
    } else {
      this.selectorArrow.setPosition(targetX, targetY);
    }
  }

  /** @private */
  _confirm() {
    // Block input during fade transitions
    if (this._transitioning) return;

    // Route to game mode sub-menu if active
    if (this._gameModeActive) {
      this._confirmGameMode();
      return;
    }

    const menuId = this.menuTexts[this.selectedIndex].getData('menuId');
    const enabled = this.menuTexts[this.selectedIndex].getData('enabled');
    if (!enabled) {
      return;
    }

    try {
      this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio fallback */ }

    switch (menuId) {
      case MenuItem.PLAY:
        this._showGameModeMenu();
        break;

      case MenuItem.CONTINUE:
        this._transitioning = true;
        this._stopMusic();
        this.cameras.main.fadeOut(400, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start(SCENE_GAME, { continuing: true });
        });
        break;

      case MenuItem.SHOP:
        this._transitioning = true;
        // Keep title music playing through the shop screen
        this.cameras.main.fadeOut(400, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start(SCENE_SHOP);
        });
        break;

      case MenuItem.CONFIG:
        this._transitioning = true;
        // Keep title music playing through the config screen
        this.cameras.main.fadeOut(400, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start(SCENE_CONFIG);
        });
        break;

      case MenuItem.MANUSCRIPTS:
        this._transitioning = true;
        this._stopMusic();
        this.cameras.main.fadeOut(400, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start(SCENE_MANUSCRIPTS);
        });
        break;

      case MenuItem.ACHIEVEMENTS:
        this._transitioning = true;
        // Keep title music playing through the achievements screen
        this.cameras.main.fadeOut(400, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start(SCENE_ACHIEVEMENTS);
        });
        break;

      case MenuItem.RECORDS:
        this._transitioning = true;
        // Keep title music playing through the records screen
        this.cameras.main.fadeOut(400, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start(SCENE_RECORDS);
        });
        break;

      case MenuItem.HELP:
        this._transitioning = true;
        // Keep title music playing through the help screen
        this.cameras.main.fadeOut(400, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start(SCENE_HELP);
        });
        break;
    }
  }

}
